import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { shareOrigin } from "../lib/api";
import BottomNav from "../components/BottomNav";
import ContactLink from "../components/ContactLink";
import { toast } from "sonner";
import { ArrowLeft, Smartphone, Plus, Link2, Hash, Copy, Trash2, Crown, X, Share2 } from "lucide-react";

export default function ManageDevices() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteModal, setInviteModal] = useState(null); // {slot: number} or null
  const [inviteData, setInviteData] = useState(null); // {code, token, expires_at}
  const [generating, setGenerating] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null); // node
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await api.get("/devices/manage");
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        // Node tried to access — bounce to settings
        navigate("/settings", { replace: true });
        return;
      }
      toast.error("Failed to load devices");
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const openInvite = (slotNumber) => {
    setInviteData(null);
    setInviteModal({ slot: slotNumber });
  };

  const generateInvite = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/devices/invite", { relation: "family" });
      setInviteData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate invite");
      setInviteModal(null);
    } finally { setGenerating(false); }
  };

  const closeInvite = () => {
    setInviteModal(null);
    setInviteData(null);
    load();
  };

  const removeDevice = async (node) => {
    try {
      await api.delete(`/devices/${node.user_id}`);
      toast.success(`${node.device_name} removed`);
      setConfirmRemove(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove");
    }
  };

  const acceptUrl = inviteData ? `${shareOrigin()}/accept/${inviteData.token}` : "";

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(acceptUrl); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };
  const copyCode = async () => {
    try { await navigator.clipboard.writeText(inviteData?.code || ""); toast.success("Code copied"); }
    catch { toast.error("Copy failed"); }
  };
  const shareLink = async () => {
    if (!navigator.share) {
      copyLink();
      return;
    }
    try {
      await navigator.share({
        title: "Join my Proximity plan",
        text: `Join my Proximity plan — tap to accept. Code: ${inviteData?.code}`,
        url: acceptUrl,
      });
    } catch {}
  };

  if (loading) {
    return <div className="min-h-screen bg-zinc-950 text-zinc-50 grid place-items-center"><div className="label-eyebrow">Loading…</div></div>;
  }

  if (!data) return null;

  const slots = [];
  for (let i = 0; i < data.max_devices; i++) {
    const dev = data.devices[i];
    slots.push({ slot: i + 1, device: dev || null });
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 pb-28">
      <header className="px-6 pt-10 pb-6 flex items-center gap-3">
        <Link to="/settings" data-testid="back-to-settings" className="size-9 rounded-full border border-white/10 grid place-items-center">
          <ArrowLeft size={16}/>
        </Link>
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Smartphone size={12}/> Devices</div>
          <h1 className="font-display font-black text-3xl tracking-tighter mt-1">Manage My Devices</h1>
        </div>
      </header>

      {/* Plan summary card */}
      <section className="px-6">
        <div className="p-5 rounded-2xl border border-[#007AFF]/30 bg-gradient-to-br from-[#007AFF]/12 to-transparent">
          <div className="flex items-center justify-between">
            <div>
              <div className="label-eyebrow">Current plan</div>
              <div className="font-display font-black text-2xl tracking-tighter mt-1" data-testid="plan-label">
                {data.tier_label || "No active plan"}
              </div>
            </div>
            <div className="text-right">
              <div className="label-eyebrow">Devices</div>
              <div className="font-mono text-xl mt-1" data-testid="device-count">
                {data.device_count} <span className="text-zinc-500">of {data.max_devices}</span>
              </div>
            </div>
          </div>
          <div className="mt-3 text-xs text-zinc-400">
            {data.open_slots > 0
              ? `${data.open_slots} open ${data.open_slots === 1 ? "slot" : "slots"} — invite to fill them.`
              : "All slots filled. Upgrade your plan or remove a device to add more."}
          </div>
        </div>
      </section>

      {/* Slots grid */}
      <section className="px-6 mt-6">
        <div className="label-eyebrow mb-3">Slots</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {slots.map(({ slot, device }) => (
            <div
              key={slot}
              data-testid={`slot-${slot}`}
              className={`p-4 rounded-2xl border transition-colors ${
                device ? "border-white/10 bg-white/[0.03]" : "border-dashed border-white/10 bg-white/[0.01] hover:bg-white/[0.04]"
              }`}
            >
              {device ? (
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-full bg-zinc-800 overflow-hidden grid place-items-center font-display font-bold">
                    {device.picture ? (
                      <img src={device.picture} alt="" className="w-full h-full object-cover"/>
                    ) : device.is_master ? (
                      <Crown size={16} className="text-[#FFCC00]"/>
                    ) : (device.device_name || "?").slice(0,1)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-[10px] text-zinc-500">#{slot}</span>
                      {device.is_master ? (
                        <div className="font-semibold text-sm truncate">{device.device_name}</div>
                      ) : (
                        <ContactLink
                          userId={device.user_id}
                          name={device.device_name}
                          testId={`device-name-${device.user_id}`}
                          className="font-semibold text-sm"
                        />
                      )}
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate">
                      {device.is_master ? "Master · You" : (device.last_seen ? "Active" : "Pending location")}
                    </div>
                  </div>
                  {!device.is_master && (
                    <button
                      data-testid={`remove-device-${device.user_id}`}
                      onClick={() => setConfirmRemove(device)}
                      className="size-8 rounded-full border border-white/10 grid place-items-center text-zinc-400 hover:text-[#FF3B30] hover:border-[#FF3B30]/40"
                      title="Remove device"
                    >
                      <Trash2 size={13}/>
                    </button>
                  )}
                </div>
              ) : (
                <button
                  data-testid={`invite-slot-${slot}`}
                  onClick={() => openInvite(slot)}
                  className="w-full flex items-center gap-3 text-left"
                >
                  <div className="size-10 rounded-full border border-dashed border-white/15 grid place-items-center text-zinc-500">
                    <Plus size={16}/>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-[10px] text-zinc-500">#{slot}</span>
                      <div className="font-semibold text-sm text-zinc-300">Invite Device</div>
                    </div>
                    <div className="text-[11px] text-zinc-500">Link or 6-digit code</div>
                  </div>
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Upgrade nudge */}
      {data.open_slots === 0 && data.tier !== "elite" && (
        <section className="px-6 mt-6">
          <Link
            to="/billing"
            data-testid="upgrade-from-devices"
            className="block p-4 rounded-2xl border border-[#FFCC00]/30 bg-[#FFCC00]/5 hover:bg-[#FFCC00]/10"
          >
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-xl bg-[#FFCC00]/15 grid place-items-center">
                <Crown size={18} className="text-[#FFCC00]"/>
              </div>
              <div className="flex-1">
                <div className="font-display font-bold">Need more slots?</div>
                <div className="text-xs text-zinc-400">Upgrade to {data.tier === "basic" ? "Proximity Plus (5 devices)" : "Proximity Elite (65 devices)"}.</div>
              </div>
              <span className="text-[#FFCC00] font-bold">→</span>
            </div>
          </Link>
        </section>
      )}

      <BottomNav />

      {/* Invite modal */}
      {inviteModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={closeInvite}>
          <div
            data-testid="invite-modal"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-3xl border border-white/10 bg-zinc-950 p-6"
          >
            <div className="flex items-center justify-between mb-1">
              <div className="label-eyebrow">Slot #{inviteModal.slot}</div>
              <button onClick={closeInvite} className="size-8 rounded-full grid place-items-center text-zinc-500 hover:text-zinc-100">
                <X size={16}/>
              </button>
            </div>
            <h2 className="font-display font-black text-2xl tracking-tighter">Invite a device</h2>
            <p className="text-sm text-zinc-400 mt-1">Share the link or the 6-digit code. The device joins instantly under your plan.</p>

            {!inviteData ? (
              <div className="mt-5 space-y-2">
                <button
                  data-testid="gen-link-btn"
                  onClick={generateInvite}
                  disabled={generating}
                  className="w-full flex items-center gap-3 p-4 rounded-2xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-left disabled:opacity-50"
                >
                  <div className="size-10 rounded-xl bg-[#007AFF]/15 grid place-items-center"><Link2 size={18} className="text-[#007AFF]"/></div>
                  <div className="flex-1">
                    <div className="font-semibold">Invite by Link</div>
                    <div className="text-xs text-zinc-500">Send via Messages, WhatsApp, Mail…</div>
                  </div>
                  <span className="text-zinc-500">→</span>
                </button>
                <button
                  data-testid="gen-code-btn"
                  onClick={generateInvite}
                  disabled={generating}
                  className="w-full flex items-center gap-3 p-4 rounded-2xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-left disabled:opacity-50"
                >
                  <div className="size-10 rounded-xl bg-[#FFCC00]/15 grid place-items-center"><Hash size={18} className="text-[#FFCC00]"/></div>
                  <div className="flex-1">
                    <div className="font-semibold">Generate 6-digit Access Code</div>
                    <div className="text-xs text-zinc-500">Read it aloud or text it</div>
                  </div>
                  <span className="text-zinc-500">→</span>
                </button>
                <div className="text-[11px] text-zinc-600 text-center pt-2">Invitations expire after 24 hours.</div>
              </div>
            ) : (
              <div className="mt-5 space-y-4">
                <div>
                  <div className="label-eyebrow mb-2 flex items-center gap-1.5"><Hash size={12}/> Access code</div>
                  <div className="flex items-center gap-2">
                    <div
                      data-testid="access-code"
                      className="flex-1 text-center font-mono font-black text-3xl tracking-[0.3em] py-4 rounded-xl border border-[#FFCC00]/30 bg-[#FFCC00]/5"
                    >
                      {inviteData.code}
                    </div>
                    <button onClick={copyCode} data-testid="copy-code-btn" className="size-12 rounded-xl border border-white/10 grid place-items-center hover:bg-white/5"><Copy size={16}/></button>
                  </div>
                </div>

                <div>
                  <div className="label-eyebrow mb-2 flex items-center gap-1.5"><Link2 size={12}/> Invitation link</div>
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="invite-link"
                      readOnly value={acceptUrl}
                      onFocus={(e)=>e.target.select()}
                      className="flex-1 bg-zinc-900 border border-white/10 rounded-lg px-3 py-2.5 text-[11px] font-mono truncate"
                    />
                    <button onClick={copyLink} data-testid="copy-link-btn" className="size-9 rounded-lg border border-white/10 grid place-items-center hover:bg-white/5"><Copy size={14}/></button>
                  </div>
                </div>

                <button
                  data-testid="share-invite-btn"
                  onClick={shareLink}
                  className="w-full bg-[#007AFF] hover:bg-[#005ec4] text-white font-bold py-3 rounded-full inline-flex items-center justify-center gap-2"
                >
                  <Share2 size={15}/> Share via apps
                </button>

                <div className="text-[11px] text-zinc-500 text-center">
                  Expires {new Date(inviteData.expires_at).toLocaleString()}. The slot is held until used or expired.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Remove confirmation */}
      {confirmRemove && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={() => setConfirmRemove(null)}>
          <div
            data-testid="remove-modal"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm rounded-3xl border border-white/10 bg-zinc-950 p-6"
          >
            <h2 className="font-display font-black text-2xl tracking-tighter">Remove device?</h2>
            <p className="text-sm text-zinc-400 mt-2">
              <span className="font-semibold text-zinc-200">{confirmRemove.device_name}</span> will be disconnected from your plan and removed from the map. The slot becomes available immediately.
            </p>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setConfirmRemove(null)} className="flex-1 py-2.5 rounded-full border border-white/10 font-bold">Cancel</button>
              <button
                data-testid="confirm-remove-btn"
                onClick={() => removeDevice(confirmRemove)}
                className="flex-1 py-2.5 rounded-full bg-[#FF3B30] text-white font-bold"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
